/// <amd-module name="Core/Medius.Core.Web/Scripts/appConfig"/>
import {
    applicationVersion,
    pdfJsVersion,
    installedAppsHash,
    documentationUrl,
    advancedSearchOperatorsDocumentationUrl,
    releaseNotesUrl,
    benchmarksUrl,
    privacyPolicyUrl,
    switchProviderUrl,
    logOffUrl,
    userVoiceEnabled,
    qaEnvironmentNoteVisible,
    currentAppDisplayVersion,
    deploymentName,
    applicationInsightsInstrumentationKey,
    applicationInsightsSamplingPercentage,
    ccfBaseUrl,
    ccfHost,
    lockManagerRepeatTime,
    lockManagerStartingAquiresNumber,
    supportPortalUrl,
    homePageSurveyUrl,
    cfoDashboardDocumentationUrl,
    knownIssuesUrl,
    payAppUrl,
    simAppUrl,
    analyticsAppUrl,
    isAccessManagementEnabled,
    loginApiUrl,
    MigratedWebApiEndpointsList,
    CanduToken,
    isEmbedded as isEmbeddedKey,
    isPolarisEnabled as isPolarisEnabledKey,
    isQACloud,
    isExtendedWebAppInsightsLoggingEnabled
} from "Core/Medius.Core.Web/Scripts/appConfigData";
import { isRedirectToBifrostEnabled } from "Core/Medius.Core.Web/Scripts/Medius/core/featureToggle";

const mfaAppUrl = "mfaAppUrl";

const appConfig: { [index: string]: any } = {};

export function register(key: string, value: any) {
    appConfig[key] = value;
}

export function registerMany(settings: { key: string, value: any }[]) {
    settings.map(setting => register(setting.key, setting.value));
}

export function registerManyWithDictionary(settings: { [key: string]: any }) {
    for (const [key, value] of Object.entries(settings)) {
        register(key, value);
    }
}

function getValue(key: string): any {
    const value = appConfig[key];

    if (value == null) {
        throw Error(`Cannot find value for ${key}.`);
    }

    return value;
}

function getValueOrDefault(key: string): any {
    const value = appConfig[key];

    if (value == null) {
        return "";
    }
    return value;
}

export function getApplicationVersion(): string {
    return getValue(applicationVersion);
}

export function getPdfJsVersion(): string {
    return getValue(pdfJsVersion);
}

export function getInstalledAppsHash(): string {
    return getValue(installedAppsHash);
}

export function getDocumentationUrl(): string {
    return getValue(documentationUrl);
}

export function getPrivacyPolicyUrl(): string {
    return getValue(privacyPolicyUrl);
}

export function getAdvancedSearchOperatorsDocumentationUrl(): string {
    return getValue(advancedSearchOperatorsDocumentationUrl);
}

export function getReleaseNotesUrl(): string {
    return getValue(releaseNotesUrl);
}

export function getBenchmarksUrl(): string {
    return getValue(benchmarksUrl);
}

export function getSupportPortalUrl(): string {
    return getValue(supportPortalUrl);
}

export function getSwitchProviderUrl(): string {
    return getValue(switchProviderUrl);
}

export function getLogOffUrl(): string {
    return getValue(logOffUrl);
}

export function isUserVoiceEnabled(): boolean {
    return getValue(userVoiceEnabled) === "True";
}

export function isQaEnvironmentNoteVisible(): boolean {
    return getValue(qaEnvironmentNoteVisible) === "True";
}

export function getCurrentAppDisplayVersion(): string {
    return getValue(currentAppDisplayVersion);
}

export function getDeploymentName(): string {
    return getValue(deploymentName);
}

export function getApplicationInsightsInstrumentationKey(): string {
    return getValue(applicationInsightsInstrumentationKey);
}

export function getApplicationInsightsSamplingPercentage(): string {
    return getValue(applicationInsightsSamplingPercentage);
}

export function getCcfBaseUrl(): string {
    return getValue(ccfBaseUrl);
}

export function getCcfHost(): string {
    return getValue(ccfHost);
}

export function getLockManagerStartingAquiresNumber(): number {
    return parseInt(getValue(lockManagerStartingAquiresNumber), 10);
}

export function getLockManagerRepeatTime(): number {
    return parseInt(getValue(lockManagerRepeatTime), 10);
}

export function getHomePageSurveyUrl(): string {
    return getValue(homePageSurveyUrl);
}

export function getCfoDashboardDocumentationUrl(): string {
    return getValue(cfoDashboardDocumentationUrl);
}

export function getKnownIssuesUrl(): string {
    return getValue(knownIssuesUrl);
}

export function getPayUrl(): string {
    return getValue(payAppUrl);
}

export function getSIMUrl(): string {
    return getValue(simAppUrl);
}

export function getAnalyticsUrl(): string {
    return getValue(analyticsAppUrl);
}

export function getLoginApiUrl(): string {
    return getValue(loginApiUrl);
}

export function getIsAccessManagementEnabled(): boolean {
    return getValue(isAccessManagementEnabled);
}

export function getMfaAppUrl(): string {
    return getValue(mfaAppUrl);
}

export function getIsQACloud() {
    return getValue(isQACloud) === true;
}

export function getIsExtendedWebAppInsightsLoggingEnabled() {
    return getValue(isExtendedWebAppInsightsLoggingEnabled) === true;
}

export function getEnabledMigratedWebApiEndpointsList(): MigratedWebApiEndpoint[] {
    const migratedEndpointsList = getValueOrDefault(MigratedWebApiEndpointsList) as string;
    return  migratedEndpointsList ? migratedEndpointsList.split(',')
        .map(entry=>entry.replace(/\s/g, ''))
        .filter(isNotEmpty)
        .map(toMigratedWebApiEndpoint): 
        [];
}

function toMigratedWebApiEndpoint(endpointWithVersion:string):MigratedWebApiEndpoint{
    return {
        endpoint: endpointWithVersion
    };
}

function isNotEmpty(s:string){
    return s!==null && s!==undefined && s!='';
}

export function getCanduToken(): string | null {
    return getValueOrDefault(CanduToken) as string | null;
}

export function isEmbedded() {
    return getValue(isEmbeddedKey) === "True";
}

export function isPolarisEnabled() {
    return getValue(isPolarisEnabledKey) === "True";
}

export interface MigratedWebApiEndpoint{
    endpoint: string,
}

// Domain migration configuration
const LEGACY_DOMAINS = ['cloud.mediusflow.com', 'email.mediusflow.com'];
const BIFROST_DOMAIN = 'app.medius.com';

// Old hash prefix → New path prefix
const REDIRECT_MAP: Record<string, string> = {
    '#Tasks/ShowDocument': 'inbox/apa/ShowDocument',
    '#/Tasks/ShowDocument': 'inbox/apa/ShowDocument',
    '#/Reports': 'reports'
};

// Hash prefixes that should NOT be redirected
const EXCLUDED_PREFIXES: string[] = [
    '#/procurement'
];

function shouldRedirectToNewDomain(): boolean {
    const onLegacyHost = LEGACY_DOMAINS.includes(window.location.hostname);
    const polarisEnabled = isPolarisEnabled();
    const embedded = isEmbedded();
    const featureEnabled = isRedirectToBifrostEnabled();
    console.log('[Polaris] shouldRedirectToNewDomain check:', { onLegacyHost, polarisEnabled, embedded, featureEnabled });
    return onLegacyHost && polarisEnabled && !embedded && featureEnabled;
}

function getTenantFromPath(): string {
    return window.location.pathname.split('/')[1] || '';
}

export function tryRedirectToBifrost(): boolean {
    if (!shouldRedirectToNewDomain()) {
        return false;
    }

    const tenant = getTenantFromPath();
    if (!tenant) {
        return false;
    }

    const hash = window.location.hash;

    // Check for excluded prefixes (e.g., procurement URLs)
    for (const excludedPrefix of EXCLUDED_PREFIXES) {
        if (hash.startsWith(excludedPrefix)) {
            console.log(`[Polaris] Skipping redirect for excluded URL: ${hash}`);
            return false;
        }
    }

    // No hash or just root - redirect to tenant root
    if (!hash || hash === '#' || hash === '#/' || hash === '#/Home') {
        const newUrl = `https://${BIFROST_DOMAIN}/${tenant}`;
        console.log(`[Polaris] Redirecting from ${window.location.href} to ${newUrl}`);
        window.location.replace(newUrl);
        return true;
    }

    // Check for specific hash mappings
    for (const [oldPrefix, newPrefix] of Object.entries(REDIRECT_MAP)) {
        if (hash.startsWith(oldPrefix)) {
            const remainder = hash.slice(oldPrefix.length); // e.g., "/209"
            const newUrl = `https://${BIFROST_DOMAIN}/${tenant}/${newPrefix}${remainder}`;
            console.log(`[Polaris] Redirecting from ${window.location.href} to ${newUrl}`);
            window.location.replace(newUrl);
            return true;
        }
    }

    // Fallback: redirect to new-experience with legacy URL as parameter
    const legacyUrl = encodeURIComponent(window.location.href);
    const newUrl = `https://${BIFROST_DOMAIN}/${tenant}/new-experience?legacyUrl=${legacyUrl}`;
    console.log(`[Polaris] Fallback redirecting from ${window.location.href} to ${newUrl}`);
    window.location.replace(newUrl);
    return true;
}
